import React, { useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Mail, Search } from "lucide-react";
import { toast } from "sonner";

export default function EnrollDialog({ open, onOpenChange, seq, contacts, accounts, onDone }) {
  const [selected, setSelected] = useState({});
  const [accountId, setAccountId] = useState("auto");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const chosenIds = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
  const visible = useMemo(() => contacts.filter((c) => !q || `${c.first_name} ${c.last_name} ${c.email} ${c.company_name}`.toLowerCase().includes(q.toLowerCase())), [contacts, q]);

  const toggleAll = (v) => setSelected(Object.fromEntries(visible.map((c) => [c.id, !!v])));

  const submit = async () => {
    if (accounts.length === 0) { toast.error("Add an email account first (Settings)."); return; }
    if (chosenIds.length === 0) { toast.error("Pick at least one contact."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/sequences/enroll", { sequence_id: seq.id, contact_ids: chosenIds, account_id: accountId === "auto" ? null : accountId });
      toast.success(`${data.enrolled} enrolled · ${data.sent} sent · ${data.failed} failed${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
      setSelected({});
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  if (!seq) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="enroll-dialog">
        <DialogHeader>
          <DialogTitle>Enroll contacts → {seq.name}</DialogTitle>
          <DialogDescription>Step 1 sends immediately; later steps are scheduled by the engine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Send from</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger data-testid="enroll-account"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Auto-rotate across inboxes (respects daily caps)</SelectItem>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.from_email} ({a.sent_today}/{a.daily_cap} today)</SelectItem>)}
              </SelectContent>
            </Select>
            {accounts.length === 0 && <div className="text-xs text-rose-500" data-testid="enroll-no-accounts">No email accounts yet — add one in Settings.</div>}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
              <Input data-testid="enroll-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter contacts..." className="pl-8 h-8 text-sm" />
            </div>
            <label className="flex items-center gap-2 text-xs text-muted-foreground"><Checkbox data-testid="enroll-select-all" checked={visible.length > 0 && visible.every((c) => selected[c.id])} onCheckedChange={toggleAll} />All visible</label>
          </div>
          <div className="border border-border rounded-md max-h-72 overflow-auto">
            {visible.map((c) => (
              <label key={c.id} className="flex items-center gap-3 px-3 py-2 border-b border-border last:border-0 cursor-pointer hover:bg-muted/40 transition-colors duration-150">
                <Checkbox data-testid={`enroll-contact-${c.id}`} checked={!!selected[c.id]} onCheckedChange={(v) => setSelected((s) => ({ ...s, [c.id]: !!v }))} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{c.first_name} {c.last_name}</div>
                  <div className="text-xs mono text-muted-foreground truncate">{c.email || "no email"}</div>
                </div>
                <div className="text-xs text-muted-foreground">{c.company_name}</div>
              </label>
            ))}
            {visible.length === 0 && <div className="p-4 text-xs text-muted-foreground">No contacts match.</div>}
          </div>
          <Button data-testid="enroll-submit" onClick={submit} disabled={loading} className="w-full"><Mail className="w-4 h-4 mr-2" />{loading ? "Enrolling..." : `Enroll ${chosenIds.length} contact${chosenIds.length === 1 ? "" : "s"}`}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
